import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Activity, PantryRow } from '../../../types';
import type { NewMealIngredient } from '../../../api/meals.api';
import MealIngredients from './MealIngredients';
import MealStopChoice, { type StopChoice } from './MealStopChoice';

export interface MealDraft {
  title: string;
  notes: string | null;
  ingredients: NewMealIngredient[];
  /** ADR-0024: applied once the meal exists. */
  stop: StopChoice;
}

interface Props {
  products: PantryRow[];
  /** The day's stops the new meal may be linked to. */
  linkable: Activity[];
  /** Resolves false when the save failed, so nothing typed is lost. */
  onSubmit: (draft: MealDraft) => Promise<boolean>;
  onCancel: () => void;
}

/**
 * Adding a meal asks for what editing one asks for — title, note and
 * ingredients — and saves it in one go. The place is not here on purpose: a
 * meal at a restaurant goes through "At a restaurant" and spends nothing from
 * the pantry (ADR-0014).
 */
export default function MealDraftForm({ products, linkable, onSubmit, onCancel }: Props) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [ingredients, setIngredients] = useState<NewMealIngredient[]>([]);
  const [stop, setStop] = useState<StopChoice>({ kind: 'none' });
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (!title.trim() || saving) return;
    setSaving(true);
    const ok = await onSubmit({ title: title.trim(), notes: notes.trim() || null, ingredients, stop });
    if (!ok) setSaving(false);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') submit();
    if (e.key === 'Escape') onCancel();
  }

  return (
    <div className="mt-1 space-y-1.5">
      <input
        autoFocus
        value={title}
        onChange={e => setTitle(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={t('meals.placeholder')}
        className="w-full bg-transparent border-b border-teal-400 dark:border-teal-500 text-sm py-1 text-slate-800 dark:text-slate-100 placeholder:text-slate-300 dark:placeholder:text-slate-600 focus:outline-none"
      />
      <input
        value={notes}
        onChange={e => setNotes(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={t('meals.notesPlaceholder')}
        className="input w-full !text-xs !px-2.5 !py-1.5"
      />
      <MealStopChoice linkable={linkable} value={stop} onChange={setStop} />
      <div className="pt-1">
        <MealIngredients
          items={ingredients.map((i, index) => ({
            key: String(index),
            name: i.name,
            label: `${String(i.amount).replace('.', ',')} ${t(`meals.units.${i.unit}`)}`,
            typed: { amount: String(i.amount).replace('.', ','), unit: i.unit },
          }))}
          products={products}
          onAdd={async (name, amount, unit) => {
            setIngredients(prev => [...prev, { name, amount, unit }]);
            return true;
          }}
          onEdit={async (key, amount, unit) => {
            setIngredients(prev => prev.map((i, index) => (String(index) === key ? { ...i, amount, unit } : i)));
            return true;
          }}
          onRemove={key => setIngredients(prev => prev.filter((_, index) => String(index) !== key))}
        />
      </div>
      <div className="flex items-center justify-end gap-1.5 pt-1">
        <button
          onClick={onCancel}
          className="px-3 py-1.5 rounded-lg text-xs text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800 cursor-pointer"
        >
          {t('common.cancel')}
        </button>
        <button
          onClick={submit}
          disabled={!title.trim() || saving}
          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-900 text-white dark:bg-white dark:text-slate-900 hover:opacity-90 disabled:opacity-30 disabled:cursor-default cursor-pointer"
        >
          {t('common.save')}
        </button>
      </div>
    </div>
  );
}
